import { useEffect, useState } from "react";
import { createGlobalStyle } from "styled-components";
import { PHONE_MEDIA, SPLIT_MEDIA, TABLE_MEDIA } from "../../Common/Orders/styles";

const DARK_GRID = "body.dark-mode .ax-app .k-grid.ax-kendo";
const ROW = ':is([role="row"], .k-master-row, .k-alt, .k-grouping-row, .k-detail-row, .k-grid-norecords, .k-group-footer, .k-footer-template)';
const DATA_ROW = `${ROW}:not(.k-grouping-row, .k-detail-row, .k-group-footer, .k-footer-template, .k-grid-norecords)`;
const BADGE_TONES = [
  "new", "processed", "order", "budget", "noorder", "neutral", "return",
  "origin-axum", "origin-masuno", "origin-axtelesale", "origin-axbot", "origin-axdeposito", "origin-axtienda",
];

const isDark = () => document.body.classList.contains("dark-mode");

// axum redefine tokens de Ax-Design sobre body.dark-mode, que le gana a .dark-mode; data-theme los vuelve a declarar en la raíz de la vista.
export const useHostTheme = () => {
  const [dark, setDark] = useState(isDark);

  useEffect(() => {
    const observer = new MutationObserver(() => setDark(isDark()));
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  return dark ? "dark" : undefined;
};

// Son las clases con las que las vistas v5 de axum sacan el marco blanco de la página.
export const useAxumFrame = () => {
  useEffect(() => {
    if (!window.orders360Embedded) return;
    const frame = document.getElementById("contenedor_main_centrado_data");
    frame?.classList.add("ax-view-no-chrome");
    document.body.classList.add("ax-view-tight-chrome");
    return () => {
      frame?.classList.remove("ax-view-no-chrome");
      document.body.classList.remove("ax-view-tight-chrome");
    };
  }, []);
};

// La página de axum trae main.css, Bootstrap 3, Kendo 2014 y su sistema v5, que usa los mismos nombres de clase y variables que Ax-Design.
export const AxumHostStyles = createGlobalStyle`
  /* Ax-Design resuelve --ax-thead-top en :root con su --ax-chrome-top de 0px: hay que volver a declararla donde cambia la barra. */
  body.ax-view-tight-chrome {
    --ax-chrome-top: var(--ax-header-h);
    --ax-thead-top: var(--ax-chrome-top);
  }

  /* El colchón sobre la pila ya lo pone el .contenedor de axum: sumado al de Ax-Design, la pila arrancaba 16px debajo de donde se pega y saltaba al scrollear. */
  body.ax-view-tight-chrome .ax-app.ax-view {
    padding-top: 0;
  }

  /* Es la altura a la que axum sube sus propios avisos para no tapar el botón de ayuda del pie v5. */
  body.has-ax-footer-v5 {
    --ax-toast-offset-y: 88px;
  }

  /* El menú del header de axum también se llama .ax-nav: con el wrap de Ax-Design se parte en dos renglones sobre la página y axum no lo pliega. */
  #ax-header > #ax-desktop-nav {
    flex-wrap: nowrap;
  }

  .ax-view-no-chrome .data .wrapper-principal {
    margin-top: 0;
    padding: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
  }

  .ax-app.ax-view {
    --ax-view-pad-x: inherit;
    --ax-view-canvas: inherit;
    --ax-panel-bg: inherit;
    --ax-card-shadow: inherit;
    --ax-card-gap: inherit;
    --ax-card-radius: inherit;
    --ax-sidebar-w: inherit;
  }

  .ax-app.ax-view .ax-page-header {
    display: block;
    margin-bottom: 0;
    padding: 0;
  }

  .ax-app.ax-view .ax-filters {
    margin-bottom: 0;
  }

  .ax-app .ax-filter-group {
    flex: none;
  }

  .ax-app .k-grid.ax-kendo thead > tr > th.k-header .k-link {
    font-weight: inherit;
  }

  /* El .ax-badge de ax-components.css vuelve a declarar el borde transparente y carga después que los tonos de Ax-Design. */
  ${BADGE_TONES.map(tone => `.ax-app .ax-badge-${tone} { border-color: var(--ax-badge-${tone}-border); }`).join("\n")}

  body.dark-mode .ax-app .ax-page-header-title {
    color: var(--ax-text-primary);
  }

  body.dark-mode .ax-app .ax-page-header-subtitle,
  body.dark-mode .ax-app .ax-filter-group-label {
    color: var(--ax-text-muted);
  }

  body.dark-mode .ax-app.ax-view .ax-filters {
    background: var(--ax-panel-bg);
    box-shadow: var(--ax-shadow-drawer);
  }

  /* main.css pinta en oscuro todo input de texto: el campo del combo queda en dos tonos y los campos pierden el borde de foco. */
  body.dark-mode .ax-app .ax-combobox input.ax-combobox-input {
    background-color: transparent;
  }

  body.dark-mode .ax-app input.ax-form-control:focus {
    border-color: var(--ax-input-focus);
  }

  @media ${TABLE_MEDIA} {
    body.dark-mode .ax-app.ax-view .ax-filters {
      box-shadow: var(--ax-card-shadow);
    }
  }

  /* ax-components.css pinta .ax-view .ax-page-header y .ax-filters con la misma especificidad que el vidrio de Ax-Design, y carga después. */
  @media ${TABLE_MEDIA} {
    @supports (backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)) {
      .ax-app.ax-view-glass .ax-page-header {
        border-color: var(--ax-glass-border);
        background: var(--ax-glass-panel-bg);
        box-shadow: 0 0 0 var(--ax-card-gap) var(--ax-view-canvas);
      }

      .ax-app.ax-view-glass .ax-filters,
      body.dark-mode .ax-app.ax-view-glass .ax-filters {
        border-color: var(--ax-glass-border);
        background: var(--ax-glass-panel-solid);
      }
    }
  }

  /* ax-components.css reparte la vista con .ax-view .ax-view-layout y esconde la perilla en todo ancho: los modos de Editar pantalla pierden por especificidad. */
  @media ${TABLE_MEDIA} {
    .ax-app.ax-view-filters-end .ax-view-layout {
      grid-template-columns: minmax(0, 1fr) var(--ax-sidebar-w);
    }

    .ax-app.ax-view-floating-filters .ax-view-layout {
      display: block;
    }

    .ax-app.ax-view-floating-filters .ax-view-sidebar {
      position: static;
    }

    .ax-app.ax-view-floating-filters .ax-filters {
      max-height: none;
    }

    .ax-app.ax-view-floating-filters .ax-filters-trigger {
      display: inline-flex;
    }

    .ax-app.ax-view-floating-filters .ax-filters-backdrop {
      display: block;
    }
  }

  /* El pie de axum va fixed con z-index alto: la marca y la ayuda quedarían sobre el cajón y los modales. */
  html.ax-scroll-lock .ax-footer-fab,
  html.ax-scroll-lock #ax-footer,
  html.ax-scroll-lock #ax-footer-brand,
  html.ax-scroll-lock footer:not(#ax-footer) {
    z-index: 1 !important;
  }

  /* En celular main.css pasa los íconos del paginador a inline-block con !important, y el chevron de Ax-Design se separa de su barra. */
  @media ${PHONE_MEDIA} {
    .ax-app .k-grid.ax-kendo > .k-pager-wrap .k-pager-nav > .k-icon {
      display: inline-flex !important;
    }
  }

  body.dark-mode .ax-app .k-grid.ax-kendo {
    border-color: var(--ax-card-border);
    background-color: var(--ax-table-bg) !important;
    color: var(--ax-text-primary);
  }

  @media ${SPLIT_MEDIA} {
    body.dark-mode .ax-app .k-grid.ax-kendo {
      background-color: transparent !important;
    }
  }

  body.dark-mode .ax-app .k-grid.ax-kendo thead > tr > th.k-header a.k-link {
    color: inherit !important;
  }

  body.dark-mode .ax-app .k-grid.ax-kendo .k-icon {
    filter: none !important;
  }

  body.dark-mode .ax-app .k-grid.ax-kendo > .k-pager-wrap .k-pager-numbers .k-state-selected {
    background: var(--ax-accent-fill) !important;
    color: var(--ax-on-accent) !important;
  }

  body.dark-mode .ax-app .ax-form-control {
    border-color: var(--ax-input-border);
    background-color: var(--ax-input-bg);
    color: var(--ax-text-primary);
  }

  body.dark-mode .ax-app .ax-form-control:hover:not(:focus, [readonly], :disabled) {
    background-color: var(--ax-input-hover);
  }

  body.dark-mode .ax-app .ax-form-control:focus {
    border-color: var(--ax-input-focus);
    background-color: var(--ax-input-bg);
  }

  /* main.css pinta en oscuro fondo y borde de toda celda de .k-grid con !important: van las de Ax-Design, en su mismo orden, también con !important. */
  ${DARK_GRID} tbody > tr${ROW} > td {
    background-color: var(--ax-table-bg) !important;
  }

  @media (hover: hover) {
    ${DARK_GRID} tbody > tr${DATA_ROW}:hover > td {
      background-color: var(--ax-state-hover) !important;
    }
  }

  ${DARK_GRID} tbody > tr${DATA_ROW}:active > td {
    background-color: var(--ax-state-pressed) !important;
  }

  ${DARK_GRID} tbody > tr${ROW}:is(.k-state-selected, .k-selected) > td,
  ${DARK_GRID} tbody > tr${ROW} > td:is(.k-state-selected, .k-selected) {
    background-color: var(--ax-state-selected) !important;
  }

  ${DARK_GRID} tbody > tr${ROW}.k-master-row:has(> td.k-hierarchy-cell > :is(.k-minus, .k-i-collapse)) > td {
    background-color: var(--ax-kendo-open-bg, var(--ax-group-well-bg)) !important;
  }

  ${DARK_GRID} tbody > tr${ROW}.k-grouping-row > td {
    background-color: var(--ax-group-head-bg) !important;
  }

  ${DARK_GRID} tbody > tr${ROW}.k-detail-row > td {
    background-color: var(--ax-group-well-bg) !important;
  }

  ${DARK_GRID} tbody > tr${ROW}:is(.k-group-footer, .k-footer-template) > td {
    background-color: var(--ax-table-header-bg) !important;
  }

  ${DARK_GRID}.ax-kendo-tree tbody > tr[data-ax-level] > td {
    border-color: transparent !important;
    background-color: transparent !important;
  }

  ${DARK_GRID}.ax-kendo-tree tbody > tr[data-ax-level]:is(.k-state-selected, .k-selected) > td {
    background-color: var(--ax-state-selected) !important;
  }

  @media ${SPLIT_MEDIA} {
    ${DARK_GRID} tbody > tr${DATA_ROW} > td:nth-child(n) {
      background-color: transparent !important;
    }

    ${DARK_GRID}:not(.ax-kendo-tree) tbody > tr.k-grouping-row > td:last-child,
    ${DARK_GRID}.ax-kendo-tree tbody > tr[data-ax-level].k-grouping-row > td:last-child {
      background-color: transparent !important;
    }

    ${DARK_GRID}.ax-kendo-tree tbody > tr[data-ax-level].k-grouping-row[data-ax-level="1"] > td:last-child {
      background-color: var(--ax-table-bg) !important;
    }

    ${DARK_GRID}.ax-kendo-tree tbody > tr[data-ax-level].k-grouping-row[data-ax-level="1"].is-open:not(.is-empty) > td:last-child {
      background-color: transparent !important;
    }
  }

  ${DARK_GRID} tbody > tr${ROW} > td.k-hierarchy-cell > .k-icon {
    color: var(--ax-text-muted) !important;
  }

  ${DARK_GRID} tbody > tr${ROW}:hover > td.k-hierarchy-cell > .k-icon,
  ${DARK_GRID} tbody > tr${ROW}.k-master-row:has(> td.k-hierarchy-cell > :is(.k-minus, .k-i-collapse)) > td.k-hierarchy-cell > .k-icon {
    color: var(--ax-row-tone) !important;
  }

  ${DARK_GRID} .ax-detail .ax-table > tbody > tr > td {
    background-color: transparent !important;
  }

  @media (hover: hover) {
    ${DARK_GRID} .ax-detail .ax-table > tbody > tr:hover > td {
      background-color: var(--ax-state-hover) !important;
    }
  }
`;
